import { IntakeFormGroupTitle, IntakeFormResponseName } from '../constants/formResponseNames';
import type {
    IntakeSummaryPdfData,
    IntakeSummaryPdfFormSubmissionField,
} from '../types/formSubmissions';
import type { QuestionSchema } from '../types/templateFormFields';
import { type ContactBlockEntry, serializeContactBlockValue } from '../utils/contactBlock';

/**
 * Dummy V2 intake covering every case the intake summary handles, for previewing the screen and
 * PDF layout and for snapshot tests. Question titles are numbered in form order and say what the
 * case is and what should happen, and answers repeat it, so anyone can spot a wrong order or a
 * row that shows when it shouldn't without knowing the rules. Fields are listed out of display
 * order on purpose: the summary must sort them.
 *
 * Conventions the tests check:
 * - Top-level titles in a group start with `N.`; they must read in increasing order
 * - A title containing "hidden when empty answers are off" must only show with empty answers on
 * - A title containing "never shows" must not show at all
 * - A title containing "screen only" must not show in the PDF
 */

const YES_NO_OPTIONS = [
    { optionLabel: 'Yes', optionValue: 'Yes' },
    { optionLabel: 'No', optionValue: 'No' },
];

const TYPES_GROUP = '1 - Question types';
const EMPTY_GROUP = '2 - Empty answers';
const TABLES_GROUP = '3 - Tables, conditionals and uploads';
const BLOCKS_GROUP = '4 - Contact blocks';

/** Sample file ids aren't real uploads, so the screen shows them as their names */
const SAMPLE_FILE_A = 'sample-file-a.pdf';
const SAMPLE_FILE_B = 'sample-file-b.pdf';

function field(
    formGroupTitle: string | null,
    displayOrder: number,
    title: string,
    questionSchema: Omit<QuestionSchema, 'name'>,
    submissionResponse: string,
    extra: Partial<IntakeSummaryPdfFormSubmissionField> = {},
): IntakeSummaryPdfFormSubmissionField {
    // Group titles start with their number, so names read like `3_1`
    const name = `${formGroupTitle?.split(' ')[0] ?? 'none'}_${displayOrder}`;
    return {
        templateFormFieldId: name,
        name,
        title,
        formGroupTitle,
        displayOrder,
        questionSchema: { ...questionSchema, name } as QuestionSchema,
        submissionResponse,
        ...extra,
    };
}

const typesFields: IntakeSummaryPdfFormSubmissionField[] = [
    // Listed out of order so the summary has to sort by display order
    field(TYPES_GROUP, 3, '3. Currency', { type: 'currency' }, '1234.5'),
    field(TYPES_GROUP, 1, '1. Text', { type: 'text' }, 'Answer to question 1'),
    field(TYPES_GROUP, 2, '2. Date', { type: 'date' }, '2024-01-15'),
    field(
        TYPES_GROUP,
        4,
        '4. Select - shows the option label, not its stored value',
        {
            type: 'select',
            selectOptions: [
                { selectItemLabel: 'Label shown instead of stored_value', selectItemValue: 'b' },
            ],
        },
        'b',
    ),
    field(
        TYPES_GROUP,
        5,
        '5. Radio - shows the option label, not its stored value',
        {
            type: 'radio',
            options: [{ optionLabel: 'Label shown instead of stored_value', optionValue: 'b' }],
        },
        'b',
    ),
    field(
        TYPES_GROUP,
        6,
        '6. Multiselect - labels joined with commas',
        {
            type: 'multiselect',
            options: [
                { optionLabel: 'First choice', optionValue: 'a' },
                { optionLabel: 'Skipped choice', optionValue: 'b' },
                { optionLabel: 'Third choice', optionValue: 'c' },
            ],
        },
        '["a","c"]',
    ),
    field(
        TYPES_GROUP,
        7,
        '7. Long text - the screen shortens it behind "See more"',
        { type: 'textarea' },
        'This answer is deliberately longer than fifty characters so the screen shortens it, and the PDF wraps it across lines inside its cell.',
    ),
    field(
        TYPES_GROUP,
        8,
        '8. Long unbroken text - the PDF breaks it so it fits the cell',
        { type: 'text' },
        'ThisAnswerHasNoSpacesAtAllSoThePdfHasToBreakItSomewhereToKeepItInsideTheValueColumn',
    ),
    field(
        TYPES_GROUP,
        9,
        '9. Critical question - green marker on screen, no marker in PDF',
        { type: 'text' },
        'Answer to a critical question',
        { isCriticalQuestion: true },
    ),
    field(
        TYPES_GROUP,
        10,
        '10. Duplicate label',
        { type: 'text' },
        'First of two questions with the same label',
    ),
    field(
        TYPES_GROUP,
        11,
        '10. Duplicate label',
        { type: 'text' },
        'Second of two questions with the same label',
    ),
];

const EMPTY_SUFFIX = 'hidden when empty answers are off';

const emptyFields: IntakeSummaryPdfFormSubmissionField[] = [
    field(EMPTY_GROUP, 1, `1. Empty text - "Not Answered", ${EMPTY_SUFFIX}`, { type: 'text' }, ''),
    field(
        EMPTY_GROUP,
        2,
        `2. Spaces only - counts as empty: "Not Answered", ${EMPTY_SUFFIX}`,
        { type: 'text' },
        '   ',
    ),
    field(
        EMPTY_GROUP,
        3,
        `3. Empty table - "Not Answered", ${EMPTY_SUFFIX}`,
        { type: 'table', columns: [{ key: 'item', label: 'Item', type: 'text' }] },
        '[]',
    ),
    field(
        EMPTY_GROUP,
        4,
        `4. Empty upload - screen only: "Not Answered", ${EMPTY_SUFFIX}`,
        { type: 'upload' },
        '',
    ),
    field(
        EMPTY_GROUP,
        5,
        '5. Answered after the empty ones - always shows',
        { type: 'text' },
        'Always shown',
    ),
];

const tablesFields: IntakeSummaryPdfFormSubmissionField[] = [
    field(
        TABLES_GROUP,
        1,
        '1. Conditional parent - answered Yes',
        { type: 'radio', options: YES_NO_OPTIONS },
        'Yes',
    ),
    field(
        TABLES_GROUP,
        2,
        '2. Conditional child table - parent said Yes, sits right under it',
        {
            type: 'table',
            isConditionalQuestion: true,
            conditionedBy: '3_1',
            conditionValue: 'Yes',
            columns: [{ key: 'item', label: 'Item', type: 'text' }],
        },
        JSON.stringify([{ item: 'Row under the Yes parent' }]),
    ),
    field(
        TABLES_GROUP,
        3,
        '3. Conditional parent - answered No',
        { type: 'radio', options: YES_NO_OPTIONS },
        'No',
    ),
    field(
        TABLES_GROUP,
        4,
        '4. Conditional child - parent said No but the answer was submitted, so it shows (the form decides)',
        {
            type: 'text',
            isConditionalQuestion: true,
            conditionedBy: '3_3',
            conditionValue: 'Yes',
        },
        'Stale answer from before the parent changed to No',
    ),
    field(
        TABLES_GROUP,
        5,
        '5. Table with every column type - blank cell shows "Not Answered" when empty answers are on',
        {
            type: 'table',
            columns: [
                { key: 'text', label: 'Text', type: 'text' },
                { key: 'date', label: 'Date', type: 'date' },
                { key: 'money', label: 'Currency', type: 'currency' },
                {
                    key: 'pick',
                    label: 'Select',
                    type: 'select',
                    selectOptions: [{ selectItemLabel: 'Option label', selectItemValue: 'opt' }],
                },
            ],
        },
        JSON.stringify([
            { text: 'Row 1', date: '2024-02-29', money: '99.9', pick: 'opt' },
            { text: 'Row 2 - date left blank', money: '1000000', pick: 'opt' },
        ]),
    ),
    field(
        TABLES_GROUP,
        6,
        '6. Table with a fully blank row - the blank row is dropped',
        { type: 'table', columns: [{ key: 'item', label: 'Item', type: 'text' }] },
        JSON.stringify([{ item: '' }, { item: 'The only row that shows' }]),
    ),
    field(
        TABLES_GROUP,
        7,
        '7. Old table answer with no table setup - columns come from the answer',
        { type: 'text' },
        JSON.stringify([{ name: 'Alex', age: 7 }]),
    ),
    field(
        TABLES_GROUP,
        8,
        '8. Upload with two files - screen lists both, PDF leaves uploads out',
        { type: 'upload' },
        `${SAMPLE_FILE_A}|${SAMPLE_FILE_B}`,
    ),
    field(
        TABLES_GROUP,
        9,
        '9. Last question in the group',
        { type: 'text' },
        'Answer to question 9',
    ),
];

const BLOCK_QUESTIONS: NonNullable<QuestionSchema['contactBlock']>['questions'] = [
    {
        name: 'block_q1',
        title: 'Block Q1 - text',
        questionSchema: { name: 'block_q1', type: 'text' },
    },
    {
        name: 'block_q2',
        title: 'Block Q2 - conditional parent',
        questionSchema: { name: 'block_q2', type: 'radio', options: YES_NO_OPTIONS },
    },
    {
        name: 'block_q3',
        title: 'Block Q3 - child of Q2, only when Q2 is Yes',
        questionSchema: {
            name: 'block_q3',
            type: 'text',
            isConditionalQuestion: true,
            conditionedBy: 'block_q2',
            conditionValue: 'Yes',
        },
    },
    {
        name: 'block_q4',
        title: 'Block Q4 - table',
        questionSchema: {
            name: 'block_q4',
            type: 'table',
            columns: [{ key: 'item', label: 'Item', type: 'text' }],
        },
    },
    {
        name: 'block_q5',
        title: 'Block Q5 - upload, screen only',
        questionSchema: { name: 'block_q5', type: 'upload' },
    },
];

const FIVE_PARTY_ENTRIES: ContactBlockEntry[] = [
    {
        entryId: 'party-1',
        contact: {
            firstName: 'Party1',
            lastName: 'Everything-answered',
            email: 'party1@example.com',
            cellPhone: '555-0001',
        },
        answers: {
            block_q1: 'Party 1 Q1',
            block_q2: 'Yes',
            block_q3: 'Party 1 Q3 - shows because Q2 is Yes',
            block_q4: JSON.stringify([{ item: 'Party 1 table row' }]),
            block_q5: SAMPLE_FILE_A,
        },
    },
    {
        entryId: 'party-2',
        contact: { firstName: 'Party2', lastName: 'Q2-is-No', email: '' },
        answers: {
            block_q1: 'Party 2 Q1',
            block_q2: 'No',
            // Unlike top-level questions, block questions are re-checked, so this stays hidden
            block_q3: 'Party 2 Q3 - never shows, Q2 is No',
        },
    },
    {
        entryId: 'party-3',
        contact: { firstName: 'Party3', lastName: 'Nothing-answered', email: '' },
        answers: {},
    },
    {
        entryId: 'party-4',
        contact: {
            firstName: 'Party4',
            lastName: 'Phone-only',
            email: '',
            cellPhone: '555-0004',
        },
        answers: { block_q1: 'Party 4 Q1' },
    },
    {
        entryId: 'party-5',
        contact: {
            firstName: 'Party5',
            lastName: 'Fifth-goes-to-second-grid',
            email: 'party5.with.a.very.long.email.address@example-domain-name.com',
        },
        answers: {
            block_q1: 'Party 5 Q1',
            block_q4: JSON.stringify([{ item: 'Party 5 row 1' }, { item: 'Party 5 row 2' }]),
        },
    },
];

function contactBlockField(
    displayOrder: number,
    title: string,
    entryLabel: string,
    entries: ContactBlockEntry[],
    extra: Partial<IntakeSummaryPdfFormSubmissionField> = {},
): IntakeSummaryPdfFormSubmissionField {
    return field(
        BLOCKS_GROUP,
        displayOrder,
        title,
        { type: 'contactInfoSection', contactBlock: { entryLabel, questions: BLOCK_QUESTIONS } },
        serializeContactBlockValue({ entries }),
        extra,
    );
}

const blocksFields: IntakeSummaryPdfFormSubmissionField[] = [
    field(BLOCKS_GROUP, 1, '1. Before the blocks', { type: 'text' }, 'Answer before the blocks'),
    contactBlockField(
        2,
        '2. Block with one entry (critical) - entry titled just "Single Party"',
        'Single Party',
        [FIVE_PARTY_ENTRIES[0]],
        { isCriticalQuestion: true },
    ),
    field(BLOCKS_GROUP, 3, '3. Between the blocks', { type: 'text' }, 'Answer between blocks'),
    contactBlockField(
        4,
        '4. Block with five entries - as columns, splits into 4 + 1',
        'Party',
        FIVE_PARTY_ENTRIES,
    ),
    contactBlockField(
        5,
        '5. Block with no entries - never shows (known gap: should say "None")',
        'Empty Party',
        [],
    ),
    field(BLOCKS_GROUP, 6, '6. After the blocks', { type: 'text' }, 'Answer after the blocks'),
];

const ungroupedFields: IntakeSummaryPdfFormSubmissionField[] = [
    field(
        null,
        1,
        '1. Question with no group - lands in "Other Information"',
        { type: 'text' },
        'Answer with no group',
    ),
];

export const SAMPLE_V2_INTAKE_SUMMARY_DATA: IntakeSummaryPdfData = {
    matterType: 'Divorce',
    contactFormValues: [
        {
            firstName: 'Client1',
            middleName: 'Every-field',
            lastName: 'Answered',
            email: 'client1@example.com',
            cellPhone: '555-1001',
            homePhone: '555-1002',
            homeAddress: 'Client 1 home address',
            homeUnit: 'Client 1 unit',
            dateOfBirth: '1980-01-15',
            gender: 'Client 1 gender',
            preferredPronouns: 'Client 1 pronouns',
            maritalStatus: 'Client 1 marital status',
            citizenshipStatus: 'Client 1 citizenship',
            occupation: 'Client 1 occupation',
            employer: 'Client 1 employer',
            employerAddress: 'Client 1 employer address',
            employerUnit: 'Client 1 employer unit',
            employerPhone: '555-1003',
            firstIdType: 'Canadian passport',
            firstIdSingle: SAMPLE_FILE_A,
        },
        {
            firstName: 'Client2',
            lastName: 'Some-fields',
            email: '',
            cellPhone: '555-2001',
            occupation: 'Client 2 occupation',
        },
        { firstName: '', lastName: '', email: 'client3-no-name-heading-falls-back@example.com' },
    ],
    formSubmissionFields: [
        ...typesFields,
        ...emptyFields,
        ...tablesFields,
        ...blocksFields,
        ...ungroupedFields,
    ],
};

/** A V1 client as stored in `user_contact_details` */
export interface SampleV1IntakeClient {
    firstName: string;
    middleName?: string;
    lastName: string;
    email?: string;
    phoneNumber?: string;
    /** `YYYY-MM-DD` */
    dateOfBirth?: string;
    maritalStatus?: string;
    occupation?: string;
    employer?: string;
}

export interface SampleV1IntakeSummaryData {
    matterType: string;
    clients: SampleV1IntakeClient[];
    /** As V1 submits them: per-client answers in a party group are joined with `|` */
    formSubmissionFields: IntakeSummaryPdfFormSubmissionField[];
}

function v1Field(
    formGroupTitle: string,
    displayOrder: number,
    name: string,
    title: string,
    questionSchema: Omit<QuestionSchema, 'name'>,
    submissionResponse: string,
    extra: Partial<IntakeSummaryPdfFormSubmissionField> = {},
): IntakeSummaryPdfFormSubmissionField {
    return {
        templateFormFieldId: `v1_${name}`,
        name,
        title,
        formGroupTitle,
        displayOrder,
        questionSchema: { ...questionSchema, name } as QuestionSchema,
        submissionResponse,
        ...extra,
    };
}

const V1_INITIAL = IntakeFormGroupTitle.INITIAL_DETAILS;
const V1_PARTIES = IntakeFormGroupTitle.PURCHASER_INFORMATION;
const V1_MAILING = IntakeFormGroupTitle.MAILING_INFO;
const V1_PROPERTY = IntakeFormGroupTitle.PROPERTY_DETAILS;

const V1_TABLE_SCHEMA: Omit<QuestionSchema, 'name'> = {
    type: 'table',
    columns: [{ key: 'item', label: 'Item', type: 'text' }],
};

/**
 * Dummy V1 Real Estate Purchase intake with two purchasers, in V1's storage format, following
 * the same title conventions as the V2 sample. Fixed field names (firstName, email, ...) are what
 * V1 keys off, so they keep their real names.
 */
export const SAMPLE_V1_INTAKE_SUMMARY_DATA: SampleV1IntakeSummaryData = {
    matterType: 'Purchase',
    clients: [
        {
            firstName: 'Purch1',
            middleName: 'Middle',
            lastName: 'Everything',
            email: 'purch1@example.com',
            phoneNumber: '555-3001',
            dateOfBirth: '1980-01-15',
            maritalStatus: 'Purchaser 1 marital status',
            occupation: 'Purchaser 1 occupation',
            employer: 'Purchaser 1 employer',
        },
        { firstName: 'Purch2', lastName: 'Some-fields', phoneNumber: '555-3002' },
    ],
    formSubmissionFields: [
        v1Field(V1_INITIAL, 1, 'text_initial_1', '1. Initial text', { type: 'text' }, 'Answer 1'),
        v1Field(
            V1_INITIAL,
            2,
            IntakeFormResponseName.ID_TYPE,
            '2. ID type in Initial details - never shows',
            { type: 'select' },
            'Passport',
        ),
        v1Field(
            V1_INITIAL,
            3,
            'date_initial_3',
            '3. Old-style date answer - shows as MM/DD/YYYY',
            { type: 'date' },
            'Tue Jan 02 2024 00:00:00 GMT-0500 (Eastern Standard Time)',
        ),
        v1Field(
            V1_INITIAL,
            4,
            'text_initial_4',
            `4. Empty answer - "Not Answered", ${EMPTY_SUFFIX}`,
            { type: 'text' },
            '',
        ),
        v1Field(
            V1_INITIAL,
            5,
            'text_initial_5',
            '5. Critical question - V1 PDF has no marker',
            { type: 'text' },
            'Answer to a critical question',
            { isCriticalQuestion: true },
        ),
        v1Field(
            V1_PARTIES,
            1,
            IntakeFormResponseName.FIRST_NAME,
            '1. First name (fixed field) - V1 reads it from the client record',
            { type: 'text' },
            'Purch1|Purch2',
        ),
        v1Field(
            V1_PARTIES,
            2,
            IntakeFormResponseName.MIDDLE_NAME,
            '2. Middle name (fixed field)',
            { type: 'text' },
            'Middle|',
        ),
        v1Field(
            V1_PARTIES,
            3,
            IntakeFormResponseName.LAST_NAME,
            '3. Last name (fixed field)',
            { type: 'text' },
            'Everything|Some-fields',
        ),
        v1Field(
            V1_PARTIES,
            4,
            IntakeFormResponseName.EMAIL,
            '4. Email (fixed field)',
            { type: 'text' },
            'purch1@example.com|',
        ),
        v1Field(
            V1_PARTIES,
            5,
            IntakeFormResponseName.PRIMARY_PHONE,
            '5. Phone (fixed field)',
            { type: 'text' },
            '555-3001|555-3002',
        ),
        v1Field(
            V1_PARTIES,
            6,
            IntakeFormResponseName.DATE_OF_BIRTH,
            '6. Date of birth (fixed field)',
            { type: 'date' },
            'Tue Jan 15 1980 00:00:00|',
        ),
        v1Field(
            V1_PARTIES,
            7,
            IntakeFormResponseName.OCCUPATION,
            '7. Occupation (fixed field)',
            { type: 'text' },
            'Purchaser 1 occupation|',
        ),
        v1Field(
            V1_PARTIES,
            8,
            'text_party_8',
            '8. Per-client text - each client has their own answer',
            { type: 'text' },
            'Purchaser 1 answer|Purchaser 2 answer',
        ),
        v1Field(
            V1_PARTIES,
            9,
            'date_party_9',
            '9. Per-client old-style dates - show as MM/DD/YYYY',
            { type: 'date' },
            'Tue Jan 02 1990 00:00:00|Wed Mar 04 1992 00:00:00',
        ),
        v1Field(
            V1_PARTIES,
            10,
            'text_party_10',
            "10. Per-client text, purchaser 2 blank - V1 can't tell hidden from empty",
            { type: 'text' },
            'Only purchaser 1 answered|',
        ),
        v1Field(
            V1_PARTIES,
            11,
            'table_party_11',
            '11. Per-client table - each client has their own rows',
            V1_TABLE_SCHEMA,
            `${JSON.stringify([{ item: 'Purchaser 1 row' }])}|${JSON.stringify([{ item: 'Purchaser 2 row' }])}`,
        ),
        v1Field(
            V1_PARTIES,
            12,
            'firstIdType',
            '12. ID type per client - never shows (its name contains firstId)',
            { type: 'select' },
            'Passport|Passport',
        ),
        v1Field(
            V1_PARTIES,
            13,
            'upload_party_13',
            '13. Per-client upload - screen only',
            { type: 'upload' },
            'sample-file-a.pdf|sample-file-b.pdf',
        ),
        v1Field(
            V1_MAILING,
            1,
            'text_mailing_1',
            '1. Mailing address',
            { type: 'text' },
            '123 Sample Street',
        ),
        v1Field(
            V1_MAILING,
            2,
            'text_mailing_2',
            `2. Empty mailing unit - "Not Answered", ${EMPTY_SUFFIX}`,
            { type: 'text' },
            '',
        ),
        v1Field(
            V1_PROPERTY,
            1,
            IntakeFormResponseName.PURCHASE_PRICE,
            '1. Purchase price (fixed field) - V1 adds "$ " without formatting',
            { type: 'currency' },
            '500000',
        ),
        v1Field(
            V1_PROPERTY,
            2,
            'currency_property_2',
            '2. Other currency question - formatted like V2',
            { type: 'currency' },
            '1234.5',
        ),
        v1Field(
            V1_PROPERTY,
            3,
            'table_property_3',
            '3. Table between questions',
            V1_TABLE_SCHEMA,
            JSON.stringify([{ item: 'Property table row' }]),
        ),
        v1Field(
            V1_PROPERTY,
            4,
            'text_property_4',
            '4. Last property question',
            { type: 'text' },
            'Answer 4',
        ),
        v1Field(
            'Group V1 does not know',
            1,
            'text_unknown_1',
            '1. Question in a group V1 does not know - never shows',
            { type: 'text' },
            'Dropped by V1',
        ),
    ],
};
